import { useState } from "react";
import {
  CopyButton,
  Notice,
  PillGroup,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { BUTTON_SMALL, LABEL, WIDGET_H2 } from "@/components/tool-kit/styles";
import { GibbsPlot } from "@/components/tools/chem-physical/gibbs-plot";
import { FailureNotice } from "@/components/tools/chem-physical/heat-shared";
import { WORKING_BOX } from "@/components/tools/chemistry/formula-view";
import { QuantityInput } from "@/components/tools/chemistry/quantity-input";
import type { GIBBS_FREE_ENERGY_CALCULATOR_STRINGS } from "@/components/tools/gibbs-free-energy-calculator";
import { useToolNumber } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  GIBBS_TERMS,
  type GibbsSolution,
  type GibbsTerm,
  MOLAR_ENERGY_UNITS,
  MOLAR_ENTROPY_UNITS,
  solveGibbs,
  splitLog10,
} from "@/lib/tool-kit/chem-physical/gibbs";
import {
  unitFactor,
  unitOptionsOf,
  unitSymbol,
} from "@/lib/tool-kit/chem-physical/heat";
import {
  fromKelvin,
  TEMPERATURE_UNITS,
  temperatureSymbol,
} from "@/lib/tool-kit/chem-physical/units";
import {
  fewestSigFigs,
  formatPlain,
  formatSig,
  superscriptInt,
} from "@/lib/tool-kit/chemistry/format";

export type GibbsStrings = typeof GIBBS_FREE_ENERGY_CALCULATOR_STRINGS;

export function gibbsMessages(strings: GibbsStrings): Record<string, string> {
  return {
    missing: strings.enterValues,
    unreadable: strings.errorUnreadable,
    nonPositive: strings.errorNonPositive,
    belowZero: strings.errorBelowZero,
    zeroEntropy: strings.errorZeroEntropy,
    resultBelowZero: strings.errorResultBelowZero,
  };
}

const DEFAULT_SIG = 4;
const TEMPERATURE_OPTIONS = TEMPERATURE_UNITS.map((u) => ({
  value: u.id,
  label: u.symbol,
}));
const EXAMPLES = [
  { id: "exampleVaporization", h: "44.01", s: "118.8", t: "298.15" },
  { id: "exampleMelting", h: "6.01", s: "22.0", t: "263.15" },
] as const;

/** K written as a plain number or as m × 10ⁿ. */
export function formatK(
  fmt: ReturnType<typeof useToolNumber>,
  log10K: number,
  sig: number
): string {
  if (log10K > -3 && log10K < 6) {
    return formatSig(fmt, 10 ** log10K, sig);
  }
  const { mantissa, exponent } = splitLog10(log10K, sig);
  const shown = fmt(mantissa, {
    minimumFractionDigits: sig - 1,
    maximumFractionDigits: sig - 1,
  });
  return `${shown} × 10${superscriptInt(exponent)}`;
}

function GibbsAnswer({
  solution,
  texts,
  units,
  sig,
  strings,
}: {
  solution: GibbsSolution;
  texts: Record<GibbsTerm, string>;
  units: { g: string; h: string; s: string; t: string };
  sig: number;
  strings: GibbsStrings;
}) {
  const fmt = useToolNumber();
  const { unknown, values } = solution;
  const p = (value: number) => formatPlain(fmt, value);
  const labels: Record<GibbsTerm, string> = {
    g: strings.termG,
    h: strings.termH,
    s: strings.termS,
    t: strings.termT,
  };
  const lists = {
    g: MOLAR_ENERGY_UNITS,
    h: MOLAR_ENERGY_UNITS,
    s: MOLAR_ENTROPY_UNITS,
  };
  const answerSymbol =
    unknown === "t"
      ? temperatureSymbol(units.t)
      : unitSymbol(lists[unknown], units[unknown]);
  const answerText = `${formatSig(fmt, solution.answer, sig)} ${answerSymbol}`;
  const base = {
    g: `${p(values.g)} J/mol`,
    h: `${p(values.h)} J/mol`,
    s: `${p(values.s)} J/(mol·K)`,
    t: `${p(values.t)} K`,
  };
  const conversions = GIBBS_TERMS.filter((term) => term !== unknown)
    .filter((term) =>
      term === "t"
        ? units.t !== "K"
        : unitFactor(lists[term], units[term]) !== 1
    )
    .map((term) =>
      t(strings.convertLine, {
        label: labels[term],
        value: texts[term].trim(),
        unit:
          term === "t"
            ? temperatureSymbol(units.t)
            : unitSymbol(lists[term], units[term]),
        converted: base[term],
      })
    );
  const rearranged = {
    g: strings.rearrangedG,
    h: strings.rearrangedH,
    s: strings.rearrangedS,
    t: strings.rearrangedT,
  }[unknown];
  const substituted = t(
    {
      g: strings.substitutedG,
      h: strings.substitutedH,
      s: strings.substitutedS,
      t: strings.substitutedT,
    }[unknown],
    {
      g: base.g,
      h: base.h,
      s: base.s,
      t: base.t,
      result: formatSig(fmt, values[unknown], sig + 1),
      unit: { g: "J/mol", h: "J/mol", s: "J/(mol·K)", t: "K" }[unknown],
    }
  );
  const needsConversion =
    unknown === "t"
      ? units.t !== "K"
      : unitFactor(lists[unknown], units[unknown]) !== 1;
  const spontaneity = {
    spontaneous: strings.spontaneous,
    nonSpontaneous: strings.nonSpontaneous,
    equilibrium: strings.atEquilibrium,
  }[solution.spontaneity];
  const crossoverText =
    solution.crossover === null
      ? strings.noCrossover
      : `${formatSig(fmt, fromKelvin(solution.crossover, units.t), sig)} ${temperatureSymbol(units.t)}`;
  const pattern = {
    allT: strings.patternAllT,
    lowT: strings.patternLowT,
    highT: strings.patternHighT,
    never: strings.patternNever,
    zero: strings.patternZero,
  }[solution.pattern];
  return (
    <>
      <StatGrid>
        <StatCard emphasis label={labels[unknown]} value={answerText} />
        <StatCard label={strings.spontaneityAtT} value={spontaneity} />
        <StatCard label={strings.crossover} value={crossoverText} />
      </StatGrid>
      <Notice>{pattern}</Notice>
      <GibbsPlot
        crossover={solution.crossover}
        h={values.h}
        s={values.s}
        strings={strings}
        t={values.t}
      />
      <div className={WORKING_BOX}>
        <p className="font-medium font-sans text-foreground">
          {strings.working}
        </p>
        <p>{strings.equation}</p>
        {unknown === "g" ? null : <p>{rearranged}</p>}
        {conversions.map((line) => (
          <p key={line}>{line}</p>
        ))}
        <p>{substituted}</p>
        {needsConversion ? (
          <p>
            {t(strings.answerLine, {
              result: `${formatSig(fmt, values[unknown], sig + 1)} ${{ g: "J/mol", h: "J/mol", s: "J/(mol·K)", t: "K" }[unknown]}`,
              answer: answerText,
            })}
          </p>
        ) : null}
        {solution.crossover === null ? null : (
          <p>
            {t(strings.crossoverLine, {
              h: base.h,
              s: base.s,
              result: `${formatSig(fmt, solution.crossover, sig + 1)} K`,
            })}
          </p>
        )}
      </div>
      <p className="text-muted-foreground text-sm">
        {t(strings.kIfStandard, {
          k: formatK(fmt, solution.log10K, sig),
          log: formatSig(fmt, solution.log10K, sig),
        })}
      </p>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copy}
        text={t(strings.copyText, {
          label: labels[unknown],
          value: answerText,
        })}
      />
    </>
  );
}

export function GibbsPanel({
  strings,
  sigChoice,
}: {
  strings: GibbsStrings;
  sigChoice: string;
}) {
  const [unknown, setUnknown] = useState<GibbsTerm>("g");
  const [text, setText] = useState<Record<GibbsTerm, string>>({
    g: "",
    h: "44.01",
    s: "118.8",
    t: "298.15",
  });
  const [unit, setUnit] = useState({ g: "kJ", h: "kJ", s: "J", t: "K" });
  const outcome = solveGibbs({ unknown, text, unit });
  const typed = GIBBS_TERMS.filter((term) => term !== unknown).map(
    (term) => text[term]
  );
  const sig =
    sigChoice === "match"
      ? fewestSigFigs(typed, DEFAULT_SIG)
      : Number(sigChoice);
  const labels: Record<GibbsTerm, string> = {
    g: strings.termG,
    h: strings.termH,
    s: strings.termS,
    t: strings.termT,
  };
  const unitLabels = {
    g: strings.unitEnergyG,
    h: strings.unitEnergyH,
    s: strings.unitEntropy,
    t: strings.unitTemperature,
  };
  const unitList = {
    g: unitOptionsOf(MOLAR_ENERGY_UNITS),
    h: unitOptionsOf(MOLAR_ENERGY_UNITS),
    s: unitOptionsOf(MOLAR_ENTROPY_UNITS),
    t: TEMPERATURE_OPTIONS,
  };
  const fill = (example: (typeof EXAMPLES)[number]) => {
    setUnit({ g: "kJ", h: "kJ", s: "J", t: "K" });
    setText({ g: "", h: example.h, s: example.s, t: example.t });
    setUnknown("g");
  };
  return (
    <>
      <PillGroup
        label={strings.solveLabel}
        name="gfe-solve"
        onChange={setUnknown}
        options={GIBBS_TERMS.map((term) => ({
          value: term,
          label: labels[term],
        }))}
        value={unknown}
      />
      <div className="flex flex-wrap items-center gap-2">
        <span className={LABEL}>{strings.tryLabel}</span>
        {EXAMPLES.map((example) => (
          <button
            className={BUTTON_SMALL}
            key={example.id}
            onClick={() => fill(example)}
            type="button"
          >
            {strings[example.id]}
          </button>
        ))}
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        {GIBBS_TERMS.map((term) => (
          <QuantityInput
            hint={term === "s" ? strings.entropyHint : undefined}
            id={`gfe-${term}`}
            key={term}
            label={
              unknown === term
                ? t(strings.answerSuffix, { label: labels[term] })
                : labels[term]
            }
            onUnitChange={(value) => setUnit({ ...unit, [term]: value })}
            onValueChange={(value) => setText({ ...text, [term]: value })}
            solved={unknown === term}
            unit={unit[term]}
            unitLabel={unitLabels[term]}
            units={unitList[term]}
            value={text[term]}
          />
        ))}
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {outcome.ok ? (
          <GibbsAnswer
            sig={sig}
            solution={outcome}
            strings={strings}
            texts={text}
            units={unit}
          />
        ) : (
          <FailureNotice
            failure={outcome}
            labels={labels}
            messages={gibbsMessages(strings)}
          />
        )}
      </section>
    </>
  );
}
